import React, { useRef, useState, useEffect } from 'react';
import { useDrop } from 'react-dnd';
import DraggableElement from './DraggableElement';
import styles from './index.module.scss';

const DroppableCanvas = ({
    config,
    selectedElementIndex,
    selectedElementIndices = [],
    onElementSelect,
    onElementChange,
    onModuleChange,
    onElementDoubleClick,
    canvasRef
}) => {
    // 如果没有传入canvasRef，则使用本地ref
    const localCanvasRef = useRef(null);
    const effectiveCanvasRef = canvasRef || localCanvasRef;
    const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

    // 轮播图自动切换
    useEffect(() => {
        if (config.is && config.is.length > 1) {
            const interval = setInterval(() => {
                setCurrentSlideIndex(prev => (prev + 1) % config.is.length);
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [config.is]);

    const [{ isOver }, drop] = useDrop({
        accept: 'ELEMENT',
        drop: (item, monitor) => {
            const delta = monitor.getDifferenceFromInitialOffset();
            const element = config.content[item.index];

            if (delta && element) {
                const deltaX = Math.round(delta.x);
                const deltaY = Math.round(delta.y);

                // 确定要操作的元素列表
                let elementsToHandle = [];

                if (selectedElementIndices.length > 1 && selectedElementIndices.includes(item.index)) {
                    // 多选模式：移动/复制所有选中的元素
                    elementsToHandle = selectedElementIndices.map(idx => ({ index: idx, element: config.content[idx] }));
                } else if (element.groupId) {
                    // 成组模式：找到同组的所有元素
                    const groupElements = config.content
                        .map((item, idx) => item.groupId === element.groupId ? idx : -1)
                        .filter(idx => idx !== -1);
                    elementsToHandle = groupElements.map(idx => ({ index: idx, element: config.content[idx] }));
                } else {
                    // 单个元素
                    elementsToHandle = [{ index: item.index, element }];
                }

                if (item.isCopy) {
                    // 复制模式：创建新元素
                    const elementsToAdd = elementsToHandle.map(({ element: el }) => {
                        const newX = Math.max(0, Math.min(el.x + deltaX, config.w - el.w));
                        const newY = Math.max(0, Math.min(el.y + deltaY, config.h - el.h));

                        const newElement = {
                            ...el,
                            x: newX,
                            y: newY
                        };

                        // 如果原元素有groupId，给复制的元素生成新的groupId
                        if (el.groupId && elementsToHandle.length > 1) {
                            // 多个元素复制时，给它们分配相同的新groupId
                            const newGroupId = `group_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
                            newElement.groupId = newGroupId;
                        } else if (el.groupId) {
                            // 单个元素复制时，移除groupId
                            delete newElement.groupId;
                        }

                        return newElement;
                    });

                    // 如果复制多个元素且它们没有groupId，为它们创建临时groupId以保持关联
                    if (elementsToAdd.length > 1 && !elementsToAdd[0].groupId) {
                        const tempGroupId = `group_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
                        elementsToAdd.forEach(el => {
                            el.groupId = tempGroupId;
                        });
                    }

                    // 修正所有复制元素的groupId，确保它们有相同的新groupId
                    if (elementsToAdd.length > 1) {
                        const finalGroupId = `group_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
                        elementsToAdd.forEach(el => {
                            if (el.groupId) {
                                el.groupId = finalGroupId;
                            }
                        });
                    }

                    // 添加新元素到配置中
                    onModuleChange({
                        content: [...config.content, ...elementsToAdd]
                    });
                } else {
                    // 移动模式：移动现有元素
                    elementsToHandle.forEach(({ index, element: el }) => {
                        const newX = Math.max(0, Math.min(el.x + deltaX, config.w - el.w));
                        const newY = Math.max(0, Math.min(el.y + deltaY, config.h - el.h));

                        onElementChange(index, {
                            x: newX,
                            y: newY
                        });
                    });
                }
            }
        },
        collect: (monitor) => ({
            isOver: monitor.isOver()
        })
    });

    drop(effectiveCanvasRef);


    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const [currentPos, setCurrentPos] = useState({ x: 0, y: 0 });
    const [isSelecting, setIsSelecting] = useState(false);

    const handleMouseDown = (e) => {
        // 如果点击的是元素或调整手柄，不处理
        if (e.target.closest('.element')) return;

        e.preventDefault();
        const rect = effectiveCanvasRef.current.getBoundingClientRect();
        const scale = rect.width / config.w;
        const x = (e.clientX - rect.left) / scale;
        const y = (e.clientY - rect.top) / scale;

        // 开启框选
        setIsSelecting(true);
        setStartPos({ x, y });
        setCurrentPos({ x, y });

        // 如果不按Ctrl键，清除之前的选择
        if (!e.ctrlKey && !e.metaKey) {
            onElementSelect(null, false);
        }
    };

    const handleMouseMove = (e) => {
        if (!isSelecting) return;
        e.preventDefault();
        const rect = effectiveCanvasRef.current.getBoundingClientRect();
        const scale = rect.width / config.w;
        const x = (e.clientX - rect.left) / scale;
        const y = (e.clientY - rect.top) / scale;
        setCurrentPos({ x, y });
    };

    const handleMouseUp = (e) => {
        if (!isSelecting) return;
        e.preventDefault();

        // 计算拖拽距离，如果距离很小就认为是点击而不是拖拽
        const dragDistance = Math.sqrt(
            Math.pow(currentPos.x - startPos.x, 2) + Math.pow(currentPos.y - startPos.y, 2)
        );

        if (dragDistance < 5) {
            // 距离很小，认为是点击空白区域，清空选择
            onElementSelect(null, false);
        } else {
            // 框选完成，计算选中的元素
            const selectionRect = {
                left: Math.min(startPos.x, currentPos.x),
                top: Math.min(startPos.y, currentPos.y),
                right: Math.max(startPos.x, currentPos.x),
                bottom: Math.max(startPos.y, currentPos.y)
            };

            // 检查哪些元素在选择框内
            const elementsInSelection = [];
            config.content.forEach((element, index) => {
                const elementRect = {
                    left: element.x,
                    top: element.y,
                    right: element.x + element.w,
                    bottom: element.y + element.h
                };

                // 检查元素是否与选择框相交
                if (elementRect.left < selectionRect.right &&
                    elementRect.right > selectionRect.left &&
                    elementRect.top < selectionRect.bottom &&
                    elementRect.bottom > selectionRect.top) {
                    elementsInSelection.push(index);
                }
            });

            // 更新选中状态
            if (elementsInSelection.length > 0) {
                elementsInSelection.forEach(index => {
                    onElementSelect(index, true); // 使用Ctrl模式添加选择
                });
            }
        }

        setIsSelecting(false);
    };

    // 渲染背景内容
    const renderBackground = () => {
        switch (config.bgType) {
            case 'carousel':
                if (config.is && config.is.length > 0) {
                    return (
                        <div className={styles.canvasSlideshow}>
                            {config.is.map((imageUrl, index) => (
                                <img
                                    key={index}
                                    src={imageUrl}
                                    alt={`轮播图 ${index + 1}`}
                                    className={`${styles.canvasSlide} ${index === currentSlideIndex ? styles.canvasSlideActive : ''}`}
                                />
                            ))}
                        </div>
                    );
                }
                break;
            case 'video':
                if (config.v) {
                    return (
                        <video
                            src={config.v}
                            className={styles.canvasVideo}
                            controls
                            loop
                            muted
                        />
                    );
                }
                break;
            case 'image':
                if (config.i) {
                    return (
                        <img
                            src={config.i}
                            alt="背景图片"
                            className={styles.canvasBackground}
                        />
                    );
                }
                break;
            case 'color':
                // 颜色背景不需要渲染额外内容，通过CSS处理
                return null;
            default:
                return null;
        }
        return null;
    };

    // 构建背景CSS属性
    const buildBackgroundBoxShadow = () => {
        const { boxShadowX, boxShadowY, boxShadowBlur, boxShadowSpread, boxShadowColor } = config.ex || {};
        if (boxShadowX !== undefined || boxShadowY !== undefined || boxShadowBlur !== undefined) {
            const x = boxShadowX || 0;
            const y = boxShadowY || 0;
            const blur = boxShadowBlur || 0;
            const spread = boxShadowSpread || 0;
            const color = boxShadowColor || '#000000';
            return `${x}px ${y}px ${blur}px ${spread}px ${color}`;
        }
        return config.ex?.boxShadow;
    };

    const buildBackgroundTransform = (scale) => {
        const { rotate, rotateX, rotateY, scaleX, scaleY, skewX, skewY } = config.ex || {};
        let userTransform = '';

        if (rotate !== undefined || rotateX !== undefined || rotateY !== undefined || scaleX !== undefined || scaleY !== undefined || skewX !== undefined || skewY !== undefined) {
            const r = rotate || 0;
            const rx = rotateX || 0;
            const ry = rotateY || 0;
            const sx = scaleX || 1;
            const sy = scaleY || 1;
            const skX = skewX || 0;
            const skY = skewY || 0;
            userTransform = `rotateX(${rx}deg) rotateY(${ry}deg) rotate(${r}deg) scale(${sx}, ${sy}) skew(${skX}deg, ${skY}deg)`;
        } else if (config.ex?.transform) {
            userTransform = config.ex.transform;
        }

        // 组合用户变换和缩放变换
        if (userTransform) {
            return `scale(${scale}) ${userTransform}`;
        }
        return `scale(${scale})`;
    };

    // 构建背景渐变样式
    const buildBackgroundGradient = () => {
        if (config.bgType === 'gradient') {
            const { bgGradientType, bgGradientStart, bgGradientEnd, bgGradientAngle } = config;
            if (bgGradientType === 'linear') {
                return `linear-gradient(${bgGradientAngle}deg, ${bgGradientStart}, ${bgGradientEnd})`;
            } else {
                return `radial-gradient(circle, ${bgGradientStart}, ${bgGradientEnd})`;
            }
        }
        return null;
    };

    // 应用样式到画布
    const canvasStyle = {
        width: config.w,
        height: config.h,
        maxWidth: '100%',
        position: 'relative',
        backgroundColor: config.bgType === 'color' ? config.bgColor : 'transparent',
        backgroundImage: config.bgType === 'gradient' ? buildBackgroundGradient() : 'none',
        ...config.ex,
        // 重写复合属性
        boxShadow: buildBackgroundBoxShadow()
    };

    // 计算缩放比例以适应容器
    const containerMaxWidth = 800;
    const scale = Math.min(1, containerMaxWidth / config.w);
    const scaledStyle = {
        ...canvasStyle,
        transform: buildBackgroundTransform(scale),
        transformOrigin: 'top left'
    };

    return (
        <div
            style={{
                width: config.w * scale,
                height: config.h * scale,
                position: 'relative'
            }}
        >
            <div
                ref={effectiveCanvasRef}
                className={styles.canvas}
                style={scaledStyle}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                {renderBackground()}

                {/* 渲染成组连接线 */}
                {config.content.length > 0 && (() => {
                    const groups = {};
                    config.content.forEach((element, index) => {
                        if (element.groupId) {
                            if (!groups[element.groupId]) {
                                groups[element.groupId] = [];
                            }
                            groups[element.groupId].push({ element, index });
                        }
                    });

                    return Object.entries(groups).map(([groupId, groupElements]) => {
                        if (groupElements.length < 2) return null;

                        // 计算组的边界框
                        const minX = Math.min(...groupElements.map(item => item.element.x));
                        const minY = Math.min(...groupElements.map(item => item.element.y));
                        const maxX = Math.max(...groupElements.map(item => item.element.x + item.element.w));
                        const maxY = Math.max(...groupElements.map(item => item.element.y + item.element.h));

                        return (
                            <div
                                key={groupId}
                                data-export-ignore="true"
                                className="group-indicator"
                                style={{
                                    position: 'absolute',
                                    left: minX - 4,
                                    top: minY - 4,
                                    width: maxX - minX + 8,
                                    height: maxY - minY + 8,
                                    border: '1px dashed #22C55E',
                                    borderRadius: '4px',
                                    pointerEvents: 'none',
                                    zIndex: 1,
                                    opacity: selectedElementIndices.some(idx =>
                                        groupElements.some(item => item.index === idx)
                                    ) ? 0.8 : 0.3
                                }}
                            />
                        );
                    });
                })()}

                {/* 框选区域指示 */}
                {isSelecting && (
                    <div
                        data-export-ignore="true"
                        className="selection-box"
                        style={{
                            left: Math.min(startPos.x, currentPos.x) + 'px',
                            top: Math.min(startPos.y, currentPos.y) + 'px',
                            width: Math.abs(currentPos.x - startPos.x) + 'px',
                            height: Math.abs(currentPos.y - startPos.y) + 'px',
                            background: 'rgba(34, 139, 34, 0.1)',
                            border: '2px dashed #228B22',
                            position: 'absolute',
                            pointerEvents: 'none',
                            zIndex: 1000
                        }}
                    />
                )}

                {/* 渲染内容元素 */}
                {config.content.map((element, index) => (
                    <DraggableElement
                        key={index}
                        element={element}
                        index={index}
                        isSelected={selectedElementIndex === index || selectedElementIndices.includes(index)}
                        isMultiSelected={selectedElementIndices.includes(index)}
                        onSelect={(ctrlKey) => onElementSelect(index, ctrlKey)}
                        onChange={(changes) => onElementChange(index, changes)}
                        onDoubleClick={onElementDoubleClick}
                        canvasWidth={config.w}
                        canvasHeight={config.h}
                    />
                ))}
            </div>
        </div>
    );
};

export default DroppableCanvas;
